import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Button,
    CircularProgress,
    Drawer,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import useApiData from "../../services/useApiData.js";
import PostPrintLabel from "../../services/PostPrintLabel.js";
import PostData from "../../services/PostData.js";
import { openPdfInNewTab } from "../../services/openPrintTab.js";
import OpenLabelRecord from "./OpenLabelRecord.jsx";
import SnackbarEx from "../Helpers/SnackbarEx";
import GridRowContextMenu, {
    buildPrintLabelShareUrl,
    copyShareUrlAction,
    useGridRowContextMenu,
    useSyncedUrlSearchQuery,
} from "../Helpers/GridRowContextMenu.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import useServerGridSort from "../Helpers/useServerGridSort.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { NEEDS_LABEL_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import {
    appDataGridDefaultProps,
    appDataGridSlotProps,
    emptySelectionModel,
    selectionIds,
} from "../Helpers/dataGridStyles.js";
import {
    catalogDataGridSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
} from "../Products/catalogGridStyles.js";
import "../Products/catalog.css";

const FILTER_FIELD_API_MAP = {
    parserItemId: "parserItemId",
    platform: "platform",
    itemName: "automatedInvoiceItemName",
    dateUploaded: "invoiceAutomationUploadDate",
};

export default function PrintLabelGrid({ initialSearch = "" }) {
    const { searchInput, setSearchInput, searchQ } = useSyncedUrlSearchQuery();
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [rowCount, setRowCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState(emptySelectionModel);
    const [singleRecSel, setSingleRecSel] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [openSnack, setOpenSnack] = useState({
        color: "",
        message: "",
        open: false,
    });
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();
    const {
        activeConditions,
        conjunction: gridConjunction,
        filterActive,
        barProps: filterBar,
    } = useGridFilter(NEEDS_LABEL_FILTER_FIELDS);

    useEffect(() => {
        if (!initialSearch) return;
        setSearchInput((prev) => (prev ? prev : initialSearch));
    }, [initialSearch, setSearchInput]);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [searchQ]);

    const rowMenuActions = useMemo(
        () => [
            copyShareUrlAction({
                buildUrl: (row) => buildPrintLabelShareUrl(row),
                disabled: (row) =>
                    !String(row?.parserItemId || row?.itemId || "").trim(),
                onCopied: () =>
                    setOpenSnack({
                        color: "success",
                        message: "Link copied.",
                        open: true,
                    }),
            }),
        ],
        []
    );

    const filtersParam = useMemo(() => {
        if (!activeConditions.length) return "";
        return JSON.stringify({
            conjunction: gridConjunction,
            conditions: activeConditions.map(({ field, operator, value }) => ({
                field: FILTER_FIELD_API_MAP[field] || field,
                operator,
                value,
            })),
        });
    }, [activeConditions, gridConjunction]);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.printLabels, {
        sortModel: [{ field: "dateUploaded", sort: "desc" }],
        columnVisibilityModel: {},
    });
    const { sortModel, sortForApi, onSortModelChange } = useServerGridSort(
        layout,
        "dateUploaded",
        "desc"
    );

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [filtersParam, sortForApi.sort, sortForApi.sortDir]);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            needsLabel: "true",
            page: String(paginationModel.page + 1),
            pageSize: String(paginationModel.pageSize),
            sort: sortForApi.sort,
            sortDir: sortForApi.sortDir,
        });
        if (searchQ) params.set("q", searchQ);
        if (filtersParam) params.set("filters", filtersParam);
        return `api/broylesllc/catalog-items?${params.toString()}`;
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        searchQ,
        filtersParam,
        sortForApi.sort,
        sortForApi.sortDir,
    ]);

    const { data, error, load: loading, refetch } = useApiData(listUrl);

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : 0);
        setRows(
            items.map((item) => ({
                id: String(item._id),
                parserItemId: item.parserItemId || item.itemId || "",
                platform: item.platform || "",
                itemName: item.automatedInvoiceItemName || "",
                dateUploaded: item.invoiceAutomationUploadDate
                    ? new Date(item.invoiceAutomationUploadDate).toLocaleDateString()
                    : "",
                dateUploadedRaw: item.invoiceAutomationUploadDate || null,
            }))
        );
    }, [data]);

    const selectedIds = selectionIds(selectionModel);
    const selectedRecords = rows
        .filter((row) => selectedIds.includes(row.id))
        .map((row) => ({ id: row.id, itemNum: row.parserItemId }));

    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(rows, appDataGridSlotProps),
        [rowContextMenuSlotProps, rows]
    );

    const baseColumns = useMemo(
        () => [
            { field: "parserItemId", headerName: "Parser Item ID", minWidth: 180, flex: 0.8 },
            { field: "platform", headerName: "Platform", minWidth: 160, flex: 0.6 },
            { field: "itemName", headerName: "Item Name", minWidth: 280, flex: 1.4 },
            { field: "dateUploaded", headerName: "Uploaded", minWidth: 130, flex: 0.5 },
        ],
        []
    );

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.draftWidths, layout.applyWidths]
    );

    const clearSelection = () => setSelectionModel(emptySelectionModel());

    const handlePrint = async () => {
        const selected = selectedRecords;
        if (!selected.length) return;
        if (selected.some((rec) => !String(rec.itemNum || "").trim())) {
            setOpenSnack({
                color: "warning",
                message: "A Parser Item ID is required to print a label.",
                open: true,
            });
            return;
        }

        setPrinting(true);
        try {
            const { data: pdf, error: printError } = await PostPrintLabel(
                selected,
                "broylesllc/print-labels"
            );
            if (pdf) {
                if (!openPdfInNewTab(pdf)) {
                    setOpenSnack({
                        color: "warning",
                        message: "Allow popups for this site to print labels.",
                        open: true,
                    });
                    return;
                }
                try {
                    const { success } = await PostData(
                        {
                            ids: selected.map((rec) => rec.id),
                            status: "Inspection",
                        },
                        "broylesllc/catalog-items/clear-needs-label",
                        null
                    );
                    if (success) {
                        clearSelection();
                        refetch();
                    } else {
                        setOpenSnack({
                            color: "warning",
                            message:
                                "Label printed, but the item is still marked as needing a label.",
                            open: true,
                        });
                    }
                } catch (err) {
                    console.error("Failed to mark printed:", err);
                }
            } else if (printError) {
                setOpenSnack({
                    color: "error",
                    message: printError?.message || "Error printing label.",
                    open: true,
                });
            }
        } finally {
            setPrinting(false);
        }
    };

    const closeDrawer = (result) => {
        if (result === "success") {
            clearSelection();
            refetch();
        }
        setSingleRecSel(null);
    };

    return (
        <>
            {error && (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 0.5 }}>
                    Failed to load items that need a label.
                </Typography>
            )}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search item, platform, or name…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {selectedIds.length > 0 ? (
                        <span className="catalog-selection-hint">
                            {selectedIds.length} selected
                        </span>
                    ) : (
                        <span className="catalog-selection-hint">
                            {rowCount.toLocaleString()} item{rowCount === 1 ? "" : "s"}
                            {filterActive || searchQ ? " matching" : ""}
                        </span>
                    )}
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={loading}
                        startIcon={<RefreshOutlinedIcon fontSize="small" />}
                        onClick={() => {
                            clearSelection();
                            refetch();
                        }}
                        sx={catalogQuietButtonSx}
                    >
                        Refresh
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        disabled={!selectedRecords.length || printing}
                        startIcon={
                            printing ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                <LocalPrintshopOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={handlePrint}
                        sx={catalogPrimaryButtonSx}
                    >
                        Print
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(loading || printing)}
                        rows={rows}
                        columns={columns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortingMode="server"
                        sortModel={sortModel}
                        onSortModelChange={onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onRowClick={(params, event) => {
                            const target = event?.target;
                            if (target?.closest?.(".MuiCheckbox-root, .MuiDataGrid-cellCheckbox")) {
                                return;
                            }
                            setSingleRecSel(params);
                        }}
                        paginationMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slotProps={gridSlotProps}
                        sx={catalogDataGridSx}
                        localeText={{
                            noRowsLabel: searchQ || filterActive
                                ? "No items match that search."
                                : "No items need a label.",
                        }}
                    />
                </div>
            </div>

            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />

            <Drawer
                anchor="right"
                open={!!singleRecSel}
                onClose={() => closeDrawer()}
                sx={{
                    "& .MuiDrawer-paper": {
                        height: "100%",
                        width: { xs: "100%", sm: "min(480px, 36vw)" },
                        minWidth: { sm: 380 },
                        borderRadius: "14px 0 0 14px",
                        overflow: "hidden",
                    },
                }}
            >
                {singleRecSel ? (
                    <OpenLabelRecord
                        key={singleRecSel.id || singleRecSel.row?.id}
                        recordId={singleRecSel}
                        closeDrawer={closeDrawer}
                    />
                ) : null}
            </Drawer>

            {openSnack.open && (
                <SnackbarEx
                    color={openSnack.color}
                    message={openSnack.message}
                    open={openSnack.open}
                    close={() => setOpenSnack({ color: "", message: "", open: false })}
                />
            )}
        </>
    );
}
